import { useState, useCallback, useRef } from 'react';
import { PersonStatus, StatusUpdateInput, IncidentRosterPerson } from '../Types/Incident';
import { submitStatusBatch } from '../Services/IncidentService';
import { normalizeApiPersonStatus } from '../Utils/headcountStatus';

// ── Types ──

export interface PendingUpdate {
  personId: string;
  status: PersonStatus;
  clientUpdatedAtUtc: string;
  clientUpdateId?: string;
}

export type FlushReason = 'timer' | 'manual' | 'navigation' | 'beforeUnload' | 'retry';

export interface FlushResult {
  success: boolean;
  error?: string;
  flushedCount: number;
}

export interface HeadcountPendingSyncState {
  pendingCount: number;
  isSyncing: boolean;
  hasPendingChanges: boolean;
  hasSyncError: boolean;
  syncError: string | null;
}

export interface UseHeadcountPendingSyncOptions {
  incidentId: string | undefined;
  onSyncStart?: () => void;
  onSyncSuccess?: (serverTimeUtc?: string) => void;
  onSyncError?: (message: string) => void;
  onRosterUpdate?: (updater: (prev: IncidentRosterPerson[]) => IncidentRosterPerson[]) => void;
  onRefreshRoster?: () => void;
}

const AUTO_SYNC_INTERVAL = 5_000;

export const useHeadcountPendingSync = (options: UseHeadcountPendingSyncOptions) => {
  const { incidentId, onSyncStart, onSyncSuccess, onSyncError, onRosterUpdate, onRefreshRoster } = options;

  const [pendingUpdates, setPendingUpdates] = useState<Map<string, PendingUpdate>>(new Map());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const pendingRef = useRef(pendingUpdates);
  pendingRef.current = pendingUpdates;

  const syncingRef = useRef(false);
  const syncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Mark a status change pending ──
  const markStatusPending = useCallback((personId: string, status: PersonStatus) => {
    setPendingUpdates((prev) => {
      const next = new Map(prev);
      next.set(personId, {
        personId,
        status,
        clientUpdatedAtUtc: new Date().toISOString(),
        clientUpdateId: crypto.randomUUID(),
      });
      return next;
    });
  }, []);

  // ── Discard all pending changes ──
  const discardPendingStatusUpdates = useCallback(() => {
    setPendingUpdates(new Map());
    setSyncError(null);
  }, []);

  // ── Core flush function ──
  const flushPendingStatusUpdates = useCallback(async (reason: FlushReason): Promise<FlushResult> => {
    const current = pendingRef.current;
    if (current.size === 0) {
      return { success: true, flushedCount: 0 };
    }
    if (!incidentId) {
      return { success: false, error: 'No incident ID', flushedCount: 0 };
    }
    if (!navigator.onLine) {
      return { success: false, error: 'You are offline. Reconnect to save pending headcount changes.', flushedCount: 0 };
    }

    // Prevent overlapping flushes
    if (syncingRef.current) {
      // Wait for in-flight flush to complete, then retry
      return new Promise<FlushResult>((resolve) => {
        const check = setInterval(() => {
          if (!syncingRef.current) {
            clearInterval(check);
            // If pending cleared by the other flush, we're done
            if (pendingRef.current.size === 0) {
              resolve({ success: true, flushedCount: 0 });
            } else {
              // Still pending — retry
              resolve(flushPendingStatusUpdates(reason));
            }
          }
        }, 100);
        // Safety: don't wait forever
        setTimeout(() => {
          clearInterval(check);
          resolve({ success: false, error: 'Flush timed out waiting for previous sync', flushedCount: 0 });
        }, 15_000);
      });
    }

    syncingRef.current = true;
    setIsSyncing(true);
    setSyncError(null);
    onSyncStart?.();

    const updates = Array.from(current.values());
    const flushedCount = updates.length;

    try {
      const batchUpdates: StatusUpdateInput[] = updates.map((u) => ({
        personId: u.personId,
        status: u.status,
        observedAtUtc: u.clientUpdatedAtUtc,
      }));
      const result = await submitStatusBatch(incidentId, batchUpdates);

      // Clear only the submitted updates (user may have added more during the request)
      setPendingUpdates((prev) => {
        const next = new Map(prev);
        updates.forEach((u) => {
          const cur = next.get(u.personId);
          if (cur && cur.clientUpdatedAtUtc === u.clientUpdatedAtUtc) {
            next.delete(u.personId);
          }
        });
        return next;
      });

      // Merge effective statuses into roster
      if (onRosterUpdate) {
        const resultMap = new Map(
          (result.responseData?.results ?? []).map((r) => [r.personId, r]),
        );
        onRosterUpdate((prev) =>
          prev.map((p) => {
            const apiResult = resultMap.get(p.personId);
            if (apiResult) {
              return {
                ...p,
                status: normalizeApiPersonStatus(apiResult.effectiveStatus),
                lastUpdatedAtUtc: result.responseData?.processedAtUtc ?? p.lastUpdatedAtUtc,
                lastUpdatedByName: 'you',
              };
            }
            const submitted = updates.find((u) => u.personId === p.personId);
            if (submitted) {
              return { ...p, status: submitted.status, lastUpdatedAtUtc: submitted.clientUpdatedAtUtc, lastUpdatedByName: 'you' };
            }
            return p;
          }),
        );
      }

      onSyncSuccess?.(result.serverTimeUtc);

      // Notify sidebar nav to refresh the Not Accounted count immediately
      window.dispatchEvent(new Event('eeas:statusChanged'));

      // Refresh roster from server for other headcounters' updates
      if (reason !== 'navigation') {
        onRefreshRoster?.();
      }

      return { success: true, flushedCount };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } }; message?: string };
      const httpStatus = axiosErr?.response?.status;
      const apiMessage = axiosErr?.response?.data?.error?.message;
      const detail = httpStatus ? `(HTTP ${httpStatus})` : (axiosErr?.message ?? 'Unknown error');
      const errorMsg = `Sync failed ${detail}. ${apiMessage ? apiMessage.substring(0, 80) : 'Changes preserved — will retry.'}`;
      console.error('[useHeadcountPendingSync] Sync failed:', detail, axiosErr?.response?.data ?? err);

      setSyncError(errorMsg);
      onSyncError?.(errorMsg);

      return { success: false, error: errorMsg, flushedCount: 0 };
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [incidentId, onSyncStart, onSyncSuccess, onSyncError, onRosterUpdate, onRefreshRoster]);

  // ── Timer management ──
  const cancelDelayedSync = useCallback(() => {
    if (syncTimerRef.current) {
      clearInterval(syncTimerRef.current);
      syncTimerRef.current = null;
    }
  }, []);

  const scheduleDelayedSync = useCallback(() => {
    cancelDelayedSync();
    syncTimerRef.current = setInterval(() => {
      flushPendingStatusUpdates('timer');
    }, AUTO_SYNC_INTERVAL);
  }, [cancelDelayedSync, flushPendingStatusUpdates]);

  // ── Derived state ──
  const state: HeadcountPendingSyncState = {
    pendingCount: pendingUpdates.size,
    isSyncing,
    hasPendingChanges: pendingUpdates.size > 0,
    hasSyncError: syncError !== null,
    syncError,
  };

  return {
    // State
    pendingUpdates,
    ...state,

    // Refs for external access
    pendingRef,

    // Actions
    markStatusPending,
    discardPendingStatusUpdates,
    flushPendingStatusUpdates,
    scheduleDelayedSync,
    cancelDelayedSync,
    clearSyncError: () => setSyncError(null),
    setPendingUpdates,

    // Timer ref for cleanup
    syncTimerRef,
  };
};
